import { Helmet } from 'react-helmet'
import { connect, useDispatch, useSelector } from 'react-redux'
import { driveDashboardSelector } from 'selectors'
import Page from 'rui/widgets/Page'
import Panel, { PanelSpacer } from 'components/layout/Panel'
import { MyStatsSection } from '../Dashboard/MyStatsSection'
import DriveSidebar from 'components/layout/DriveSidebar'
import Avatar from 'rui/widgets/Avatar'
import StarRating from 'components/misc/StarRating'
import styled from 'styled-components/macro'
import { badgeRanking } from 'util/legacy'
import { media } from 'styles/mixins'
import { h5, body2 } from 'styles/mixins/textstyles'
import TopBadges from 'components/misc/TopBadges'
import { getProfilePhoto } from 'util/profile'
import { getVehicleDisplayName } from 'util/vehicle'
import MyAchievements from '../Dashboard/MyAchievements'
import GetMoreGigs from '../Dashboard/GetMoreGigs'
import { getActiveVehicleId } from 'util/vehicle'
import { SenderRecognitionsSection } from '../Dashboard/SenderRecognitions/SenderRecognitionsSection'
import { AccountStatusPanel } from '../Dashboard/AccountStatus'
import { fetchSenderRecognitions } from 'actions/senderRecognitions'
import React from 'react'
import { FeatureFlag } from 'Optimizely'
import { useLocalization } from 'localization'

const AvatarBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 5rem;

  ${media.mobile`
    margin-right: 0;
    justify-content: center;
    width: 100%;
  `}
`

const Profile = styled.div`
  display: flex;
  align-items: flex-start;

  ${media.mobile`
    flex-direction: column;
  `}
`

const BioBox = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  width: 100%;
  justify-content: center;
`

const Name = styled.div`
  ${(props) => h5(props.theme.colors.black)}
  font-weight: 600;
`

const NameAndVehicle = styled.div`
  overflow: hidden;
  text-overflow: ellipses;
  white-space: nowrap;
  margin-bottom: 0.8rem;
`

const NameAndBadges = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  ${media.mobile`
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-top: .8rem;
  `}
`

const Bio = styled.div`
  ${(props) => body2(props.theme.colors.black, 'medium')}

  ${media.mobile`
    text-align: center;
  `}
`

const Vehicle = styled.div`
  ${(props) => body2(props.theme.colors.black, 'medium')}
  text-transform: capitalize;
`

const StyledAvatar = styled(Avatar)`
  margin-bottom: 1.2rem;
`

export function completedBadges(profile, badges) {
  return badges.length
    ? profile.badges.filter((b) => b.percent_complete === 100).map((c) => badges.find((b) => b.id === c.id))
    : []
}

export function displayVehicle(vehiclesList, activeVehicleId) {
  // Ideally driver has active vehicle set
  if (activeVehicleId) {
    const vehicle = vehiclesList.find((v) => v.details.id == activeVehicleId)
    if (vehicle) {
      return getVehicleDisplayName(vehicle)
    }
  }

  // if no active vehicle, return empty string
  return ''
}

function bio(profile) {
  const { biography, first_name } = profile

  return biography || `Hey, I'm ${first_name}. I'm new to Roadie and haven't written my bio yet.`
}

const HIDDEN_BADGES = ['Extra Equipped']

const SenderRecognitionsFetcher = ({ enabled }) => {
  const dispatch = useDispatch()

  React.useEffect(() => {
    if (enabled) {
      fetchSenderRecognitions()(dispatch)
    }
  }, [enabled, dispatch])

  return null
}

const DriveDashboardPage = (props) => {
  const { t } = useLocalization()
  const isLocked = useSelector((state) => state.isLocked)
  const lockReason = useSelector((state) => state.lockReason)
  const vehicles = useSelector((state) => state.vehicles)

  const completedUserBadges = completedBadges(props.profile, props.badges)
  const visibleBadges = completedUserBadges.filter((badge) => !HIDDEN_BADGES.includes(badge.name))
  const rankedBadges = badgeRanking(visibleBadges)

  const activeVehicleId = getActiveVehicleId(vehicles)
  const displayVehicleString = displayVehicle(vehicles, activeVehicleId)

  return (
    <Page sidebar={<DriveSidebar />}>
      <Helmet>
        <title>{t('dweb_drive_dashboard_my_dashboard')}</title>
      </Helmet>
      <Panel>
        <Profile>
          <AvatarBox>
            <StyledAvatar size="80" url={getProfilePhoto(props.profile)} />
            <StarRating rating={props.profile.average_rating} ratingCount={props.profile.rating_count} />
          </AvatarBox>
          <BioBox>
            <NameAndBadges>
              <NameAndVehicle>
                <Name>
                  {props.profile.first_name} {props.profile.last_name[0]}.
                </Name>
                <Vehicle>{displayVehicleString}</Vehicle>
              </NameAndVehicle>
              <TopBadges badges={rankedBadges} />
            </NameAndBadges>
            <Bio>{bio(props.profile)}</Bio>
          </BioBox>
        </Profile>
      </Panel>
      <PanelSpacer />
      <MyStatsSection />
      <FeatureFlag feature="sender_recognitions">
        {(enabled) => (
          <>
            <SenderRecognitionsFetcher enabled={enabled} />
            {enabled && (
              <>
                <SenderRecognitionsSection />
                <PanelSpacer />
              </>
            )}
          </>
        )}
      </FeatureFlag>
      {isLocked && lockReason?.reason == 'permanent' && (
        <>
          <AccountStatusPanel lockReason={lockReason} />
          <PanelSpacer />
        </>
      )}
      <GetMoreGigs />
      <PanelSpacer />
      <MyAchievements badges={rankedBadges} />
    </Page>
  )
}

export default connect(driveDashboardSelector)(DriveDashboardPage)
